<template>
  <div
    class="bg-login-image min-h-screen flex flex-col items-center justify-center bg-cover bg-center relative"
  >
    <div class="absolute inset-0 bg-black/50"></div>

    <!-- Contenedor del formulario -->
    <div
      class="relative flex flex-col w-full max-w-lg lg:max-w-md xl:max-w-xl gap-6 px-6 py-8 sm:px-12 md:px-16 m-auto bg-white/80 rounded-lg shadow-lg dark:bg-gray-800"
    >
      <div class="flex justify-center items-center">
        <img src="../../../assets/logo.png" class="w-28" />
      </div>

      <!-- Barra de progreso -->
      <div class="w-full bg-gray-200 rounded-full h-4 mb-6">
        <div
          class="bg-green-500 h-4 rounded-full"
          :style="{
            width:
              ((currentStep + 1) /
                (questionsShown.length + (mostrarConsentimientoBase ? 1 : 0))) *
                100 +
              '%',
          }"
        ></div>
      </div>

      <!-- Contenedor de preguntas -->
      <div
        v-for="(question, index) in questionsShown"
        :key="question.id"
        v-show="currentStep === index"
        class="question-step"
      >
        <!-- Etiqueta de la pregunta -->
        <label
          :for="question.id"
          class="block text-2xl font-semibold text-gray-900 text-center mb-6"
        >
          {{ question.label }}
        </label>

        <!-- FULL NAME -->
        <div v-if="question.type === 'fullName'">
          <input
            type="text"
            :id="question.id"
            v-model="answers[question.id]"
            class="w-full border-b-2 text-lg focus:outline-none p-2 text-center rounded"
            placeholder="Nombre y Apellido"
          />
          <p
            v-if="validationErrors[question.id]"
            class="text-red-600 text-sm mt-1 text-center"
          >
            {{ validationErrors[question.id] }}
          </p>
        </div>

        <!-- TEXTAREA -->
        <div v-else-if="question.type === 'textarea'">
          <textarea
            :id="question.id"
            v-model="answers[question.id]"
            rows="4"
            :placeholder="question.placeholder || ''"
            :required="question.required"
            :class="[
              'w-full border-b-2 text-lg focus:outline-none p-2 text-center rounded',
              validationErrors[question.id]
                ? 'border-red-600 focus:border-red-600'
                : 'border-green-500 focus:border-green-700',
            ]"
          ></textarea>
          <p
            v-if="validationErrors[question.id]"
            class="text-red-600 text-sm mt-1 text-center"
          >
            {{ validationErrors[question.id] }}
          </p>
        </div>

        <!-- RADIO -->
        <div v-else-if="question.type === 'radio'">
          <div class="flex flex-wrap justify-center space-x-4">
            <label
              v-for="option in question.options"
              :key="option.value"
              class="flex items-center space-x-2"
            >
              <input
                type="radio"
                :name="question.id"
                :value="option.value"
                v-model="answers[question.id]"
                class="form-radio text-green-600"
                @change="handleRadioChange(question.id)"
              />
              <span class="text-lg">{{ option.label }}</span>
            </label>
          </div>
          <p
            v-if="validationErrors[question.id]"
            class="text-red-600 text-sm mt-1 text-center"
          >
            {{ validationErrors[question.id] }}
          </p>
        </div>

        <!-- DATE -->
        <div v-else-if="question.type === 'date'">
          <input
            type="date"
            :id="question.id"
            v-model="answers[question.id]"
            :class="[
              'w-full border-b-2 text-lg focus:outline-none p-2 text-center rounded',
              validationErrors[question.id]
                ? 'border-red-600 focus:border-red-600'
                : 'border-green-500 focus:border-green-700',
            ]"
            :required="question.required"
            @change="onChange(question)"
          />
          <p
            v-if="validationErrors[question.id]"
            class="text-red-600 text-sm mt-1 text-center"
          >
            {{ validationErrors[question.id] }}
          </p>
        </div>

        <!-- MULTI-INPUT -->
        <div
          v-else-if="question.type === 'multi-input'"
          class="flex flex-col space-y-4"
        >
          <div class="flex flex-wrap gap-4">
            <div
              v-for="input in question.inputs"
              :key="input.id"
              class="flex-1 min-w-[120px]"
            >
              <label
                :for="input.id"
                class="block text-sm font-medium text-gray-700 text-center"
              >
                {{ input.label }}
              </label>
              <input
                :type="input.type || 'text'"
                :id="input.id"
                v-model="answers[input.id]"
                @change="onChange({ id: input.id })"
                :placeholder="input.placeholder || ''"
                :class="[
                  'w-full border-b-2 text-lg focus:outline-none text-center rounded',
                  validationErrors[input.id]
                    ? 'border-red-600 focus:border-red-600'
                    : 'border-green-500 focus:border-green-700',
                ]"
              />
              <p
                v-if="validationErrors[input.id]"
                class="text-red-600 text-sm mt-1 text-center"
              >
                {{ validationErrors[input.id] }}
              </p>
            </div>
          </div>
        </div>

        <!-- INPUT POR DEFECTO -->
        <div v-else>
          <input
            :type="question.inputType || 'text'"
            :id="question.id"
            v-model="answers[question.id]"
            :readonly="question.readOnly || false"
            :placeholder="question.placeholder || ''"
            :required="question.required"
            :class="[
              'w-full border-b-2 text-lg focus:outline-none p-2 text-center rounded',
              validationErrors[question.id]
                ? 'border-red-600 focus:border-red-600'
                : 'border-green-500 focus:border-green-700',
            ]"
          />
          <p
            v-if="validationErrors[question.id]"
            class="text-red-600 text-sm mt-1 text-center"
          >
            {{ validationErrors[question.id] }}
          </p>
        </div>
      </div>

      <!-- Paso Consentimiento Base (fuera del loop) -->
      <div
        v-if="
          mostrarConsentimientoBase && currentStep === questionsShown.length
        "
        class="question-step"
      >
        <h3 class="text-xl font-bold mb-4 text-center">
          Consentimiento Informado (General)
        </h3>

        <div
          class="bg-white p-4 border rounded max-h-60 overflow-y-scroll text-sm text-gray-700 consent-box"
          v-html="consentimientoBaseHTML"
        ></div>

        <label class="block mt-4 text-center">
          <input
            type="checkbox"
            v-model="consentimientoAceptado"
            class="mr-2"
          />
          Confirmo que he leído y acepto el consentimiento informado general
        </label>

        <p
          v-if="
            !consentimientoAceptado && validationErrors['consentimientoBase']
          "
          class="text-red-600 text-sm mt-1 text-center"
        >
          {{ validationErrors["consentimientoBase"] }}
        </p>
      </div>

      <!-- Navegación -->
      <div class="mt-6 flex justify-between">
        <button
          @click="prevStep"
          class="flex items-center px-6 py-3 bg-gray-300 text-gray-700 text-lg font-semibold rounded-md shadow hover:bg-gray-400"
          :disabled="currentStep === 0"
        >
          <i class="fas fa-arrow-left mr-2"></i> Anterior
        </button>

        <button
          @click="validateAndNextStep"
          :disabled="loading"
          :class="[
            'flex items-center px-6 py-3 text-lg font-semibold rounded-md shadow',
            currentStep === questionsShown.length
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : validationErrors[currentQuestionId]
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-green-600 text-white hover:bg-green-700',
          ]"
        >
          {{
            currentStep === questionsShown.length ? "Finalizar" : "Siguiente"
          }}
          <i class="fas fa-arrow-right ml-2"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { addDoc, collection } from "firebase/firestore";
import { db } from "../../../firebase";

// Consentimiento GENERAL (para todos los servicios la primera vez)
import {
  CONSENTIMIENTO_GENERAL_TXT,
  CONSENTIMIENTO_GENERAL_VERSION,
  CONSENTIMIENTO_GENERAL_ID,
} from "@/utils/consentimientoGeneral";

// (Tu consentimiento láser lo mantienes para la etapa de agendar)
import { CONSENTIMIENTO_DEPILACION_LASER } from "@/utils/consentimientos";
import { questions, buildEmptyAnswers } from "@/utils/questions.js";

const buildInitialAnswers = () => ({
  ...buildEmptyAnswers(),
  nombre: "",
  apellido: "",
});

export default {
  name: "SolicitarPaciente",
  data() {
    const initialAnswers = buildInitialAnswers();
    return {
      fullName: "",
      loading: false,
      currentStep: 0,
      answers: initialAnswers,
      validationErrors: {},

      // Consentimiento Base (para todos los servicios la primera vez)
      mostrarConsentimientoBase: true, // Si tienes verificación de “primera vez”, cámbialo dinámicamente
      consentimientoAceptado: false,
      consentimientoBaseHTML: "", // preview renderizado con placeholders

      // Si lo necesitas para vista o logs:
      consentimientoTextoLaser: CONSENTIMIENTO_DEPILACION_LASER,

      questions,
    };
  },

  computed: {
    esAdulto() {
      const n = Number(this.answers.edad || 0);
      return Number.isFinite(n) && n >= 18;
    },
    esMenor() {
      const n = Number(this.answers.edad || 0);
      return Number.isFinite(n) && n > 0 ? n < 18 : false;
    },
    // <- clave: usar arreglo filtrado
    questionsShown() {
      if (this.esAdulto) {
        return this.questions.filter(
          (q) => !["respLicencia", "respParentesco"].includes(q.id)
        );
      }
      return this.questions;
    },
    currentQuestionId() {
      return this.questionsShown[this.currentStep]?.id || "";
    },
  },

  watch: {
    "answers.fechaNacimiento"(v) {
      const edad = this.calcEdad(v);
      this.answers.edad = Number.isFinite(edad) ? edad : null;

      if (this.esAdulto) {
        this.answers.respLicencia = "";
        this.answers.respParentesco = "";
        delete this.validationErrors.respLicencia;
        delete this.validationErrors.respParentesco;

        //  CORREGIR currentStep si apunta a paso eliminado
        const shown = this.questionsShown.map((q) => q.id);
        const currentId = this.currentQuestionId;

        if (!shown.includes(currentId)) {
          // Salta al siguiente paso válido (si actual no está en la lista)
          this.currentStep = Math.max(
            0,
            shown.findIndex(
              (id) => id !== "respLicencia" && id !== "respParentesco"
            )
          );
        }
      }

      this.renderConsentimientoBase();
    },

    //  NUEVO: corrige el currentStep si queda desfasado
    questionsShown(newQuestions) {
      if (this.currentStep >= newQuestions.length) {
        console.warn("Corrigiendo currentStep fuera de rango");
        this.currentStep = newQuestions.length - 1;
      }
    },

    //  Mantenemos los demás watchers
    fullName() {
      this.renderConsentimientoBase();
    },
    answers: {
      deep: true,
      handler() {
        this.renderConsentimientoBase();
      },
    },
    "answers.licenciaCliente"(val) {
      console.log("licenciaCliente cambió a:", val);
    },
  },

  methods: {
    resetAnswers() {
      const fresh = buildInitialAnswers();
      Object.keys(this.answers).forEach((key) => {
        if (!(key in fresh)) {
          delete this.answers[key];
        }
      });
      Object.entries(fresh).forEach(([key, value]) => {
        this.answers[key] = value;
      });
    },
    // ==== Utilidades ====
    calcEdad(isoDate) {
      if (!isoDate) return "";
      const d = new Date(isoDate);
      if (Number.isNaN(d.getTime())) return "";
      const today = new Date();
      let edad = today.getFullYear() - d.getFullYear();
      const m = today.getMonth() - d.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < d.getDate())) edad--;
      return String(Math.max(0, edad));
    },
    fmtFechaLocal(iso) {
      try {
        return new Date(iso).toLocaleDateString("es-PR");
      } catch {
        return iso || "";
      }
    },
    todayISO() {
      const d = new Date();
      return d.toISOString();
    },
    sha1(text) {
      // hash base simple (no-crypto) para auditoría ligera
      // NOTA: si necesitas hash real, usa librería crypto en el backend.
      let h = 0,
        i,
        chr;
      for (i = 0; i < text.length; i++) {
        chr = text.charCodeAt(i);
        h = (h << 5) - h + chr;
        h |= 0;
      }
      return String(h);
    },
    replacePlaceholders(base, map) {
      return base
        .replaceAll(
          "{{CLIENTE_NOMBRE_COMPLETO}}",
          map.CLIENTE_NOMBRE_COMPLETO || "_______________________________"
        )
        .replaceAll(
          "{{CLIENTE_LICENCIA}}",
          map.CLIENTE_LICENCIA || "________________"
        )
        .replaceAll(
          "{{FECHA_NACIMIENTO}}",
          map.FECHA_NACIMIENTO || "__________"
        )
        .replaceAll(
          "{{FIRMA_NOMBRE}}",
          map.FIRMA_NOMBRE || "_______________________________"
        )
        .replaceAll(
          "{{FECHA_FIRMA}}",
          map.FECHA_FIRMA || this.fmtFechaLocal(this.todayISO())
        )
        .replaceAll("{{TUTOR_PARENTEZCO}}", map.TUTOR_PARENTEZCO || "")
        .replaceAll("{{TUTOR_LICENCIA}}", map.TUTOR_LICENCIA || "");
    },

    // Render del consentimiento base (HTML) usando TXT + saltos simples
    renderConsentimientoBase() {
      if (!this.mostrarConsentimientoBase) {
        this.consentimientoBaseHTML = "";
        return;
      }
      const map = {
        CLIENTE_NOMBRE_COMPLETO: (this.fullName || "").trim(),
        CLIENTE_LICENCIA: (this.answers.licenciaCliente || "").trim(),
        FECHA_NACIMIENTO: this.fmtFechaLocal(
          this.answers.fechaNacimiento || ""
        ),
        FIRMA_NOMBRE: this.esMenor
          ? `Responsable de ${this.fullName || ""}`
          : this.fullName || "",
        FECHA_FIRMA: this.fmtFechaLocal(this.todayISO()),
        TUTOR_PARENTEZCO: this.esMenor ? this.answers.respParentesco || "" : "",
        TUTOR_LICENCIA: this.esMenor ? this.answers.respLicencia || "" : "",
      };

      const textoRelleno = this.replacePlaceholders(
        CONSENTIMIENTO_GENERAL_TXT,
        map
      );
      // Render simple a HTML con saltos (si tu util trae HTML, puedes usarlo directo)
      this.consentimientoBaseHTML = textoRelleno
        .split("\n")
        .map((l) => `<p>${l.replace(/ {2}/g, "&nbsp;&nbsp;")}</p>`)
        .join("");
      return textoRelleno; // por si lo necesitas
    },

    onChange(question) {
      if (question.id === "fechaNacimiento") {
        this.answers.edad = this.calcEdad(this.answers.fechaNacimiento);
      }
      if (question.id === "email" && this.answers.confirmEmail) {
        this.validationErrors.confirmEmail = this.emailsIguales(
          this.answers.email,
          this.answers.confirmEmail
        )
          ? ""
          : "El correo no coincide";
      }
      if (question.id === "confirmEmail" && this.answers.email) {
        this.validationErrors.confirmEmail = this.emailsIguales(
          this.answers.email,
          this.answers.confirmEmail
        )
          ? ""
          : "El correo no coincide";
      }
    },

    handleRadioChange(id) {
      if (id === "tratamientosArea" && this.answers.tratamientosArea !== "si") {
        this.answers.detalleTratamientosArea = "";
      }
    },

    emailsIguales(a, b) {
      return (
        String(a || "")
          .trim()
          .toLowerCase() ===
        String(b || "")
          .trim()
          .toLowerCase()
      );
    },

    prevStep() {
      if (this.currentStep > 0) this.currentStep--;
    },

    // ===== Validación y avance =====
    validateAndNextStep() {
      const totalPreguntas = this.questionsShown.length;
      this.validationErrors = { ...this.validationErrors };

      if (this.currentStep < totalPreguntas) {
        const currentQuestion = this.questionsShown[this.currentStep];
        const qid = currentQuestion?.id || "";
        const edadNum = Number(this.answers.edad || 0);

        // Verificación defensiva: evita procesar pasos no válidos para adultos
        if (edadNum >= 18 && ["respLicencia", "respParentesco"].includes(qid)) {
          console.warn(`Paso '${qid}' no corresponde a adultos. Saltando.`);
          this.currentStep++;
          return;
        }

        if (!currentQuestion) {
          console.warn("Paso inválido:", this.currentStep);
          return;
        }

        // FULL NAME
        if (qid === "fullName") {
          const fullName = this.answers.fullName?.trim() || "";
          if (!fullName) {
            this.validationErrors.fullName = "Este campo es obligatorio.";
            return;
          }

          const parts = fullName.split(/\s+/);
          const nombre = parts.shift();
          const apellido = parts.join(" ");

          this.answers.nombre = nombre || "";
          this.answers.apellido = apellido || "";
          this.fullName = fullName;

          if (!nombre || !apellido) {
            this.validationErrors.fullName =
              "Por favor, ingrese tanto el nombre como el apellido.";
            return;
          }
        }

        // EMAIL
        if (qid === "email") {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
          if (!emailRegex.test(this.answers.email || "")) {
            this.validationErrors.email = "Correo electrónico inválido.";
            return;
          }
        }

        // CONFIRM EMAIL
        if (qid === "confirmEmail") {
          if (
            !this.emailsIguales(this.answers.email, this.answers.confirmEmail)
          ) {
            this.validationErrors.confirmEmail = "Los correos no coinciden.";
            return;
          }
        }

        // TELÉFONO
        if (qid === "telefono") {
          const phoneRegex = /^[0-9]+$/;
          if (!phoneRegex.test(String(this.answers.telefono || ""))) {
            this.validationErrors.telefono =
              "El teléfono solo puede contener números.";
            return;
          }
        }

        // FECHAS
        if (currentQuestion.inputType === "date") {
          const value = this.answers[qid];
          const dateValue = value ? new Date(value) : null;
          if (!value || isNaN(dateValue.getTime())) {
            this.validationErrors[qid] = "Fecha inválida.";
            return;
          }
        }

        // CAMPOS REQUERIDOS
        if (
          (currentQuestion.type === "text" ||
            currentQuestion.type === "textarea") &&
          currentQuestion.required &&
          !String(this.answers[qid] || "").trim()
        ) {
          this.validationErrors[qid] = "Este campo es obligatorio.";
          return;
        }

        // RADIO
        if (currentQuestion.type === "radio" && currentQuestion.required) {
          if (!this.answers[qid]) {
            this.validationErrors[qid] = `Seleccione una opción.`;
            return;
          }
        }

        // LICENCIA CLIENTE
        if (qid === "licenciaCliente") {
          const lic = String(this.answers.licenciaCliente || "").trim();
          console.log("Validando licenciaCliente:", lic, "Edad:", edadNum);

          if (!lic || lic.length < 5) {
            this.validationErrors.licenciaCliente =
              "Licencia requerida (mín. 5 caracteres).";
            return;
          }
        }

        // LICENCIA RESPONSABLE (si llegara a mostrarse por error)
        if (qid === "respLicencia" && edadNum >= 18) {
          console.warn(
            "⚠️ Este paso no debe mostrarse para adultos. Saltando."
          );
          this.currentStep++;
          return;
        }

        if (qid === "respLicencia") {
          const val = String(this.answers.respLicencia || "").trim();
          if (!val || val.length < 5) {
            this.validationErrors.respLicencia =
              "Licencia del responsable inválida.";
            return;
          }
        }

        // PARENTESCO RESPONSABLE (si llegara a mostrarse por error)
        if (qid === "respParentesco" && edadNum >= 18) {
          console.warn(
            "⚠️ Este paso no debe mostrarse para adultos. Saltando."
          );
          this.currentStep++;
          return;
        }

        if (qid === "respParentesco") {
          const val = String(this.answers.respParentesco || "").trim();
          if (!val) {
            this.validationErrors.respParentesco =
              "Parentesco del responsable requerido.";
            return;
          }
        }

        // ✅ Si todo ok, avanza
        this.currentStep++;
      } else if (
        this.currentStep === totalPreguntas &&
        this.mostrarConsentimientoBase
      ) {
        if (!this.consentimientoAceptado) {
          this.validationErrors.consentimientoBase =
            "Debe aceptar el consentimiento informado.";
          return;
        }
        this.submitForm();
      } else {
        this.submitForm();
      }
    },

    // ===== Envío =====
  async submitForm() {
    this.loading = true;
    try {
        const answersPayload = { ...this.answers };
        const servicioSeleccionado = answersPayload.servicio;
        const edadNum = Number(answersPayload.edad || 0);
        const esMenor = Number.isFinite(edadNum) ? edadNum < 18 : false;

        const consentMap = {
          CLIENTE_NOMBRE_COMPLETO: (this.fullName || "").trim(),
          CLIENTE_LICENCIA: (answersPayload.licenciaCliente || "").trim(),
          FECHA_NACIMIENTO: this.fmtFechaLocal(
            answersPayload.fechaNacimiento || ""
          ),
          FIRMA_NOMBRE: esMenor
            ? `Responsable de ${this.fullName || ""}`
            : this.fullName || "",
          FECHA_FIRMA: this.fmtFechaLocal(this.todayISO()),
          TUTOR_PARENTEZCO: esMenor ? answersPayload.respParentesco || "" : "",
          TUTOR_LICENCIA: esMenor ? answersPayload.respLicencia || "" : "",
        };
        const textoRellenoTXT = this.replacePlaceholders(
          CONSENTIMIENTO_GENERAL_TXT,
          consentMap
        );
        const hashBase = this.sha1(
          `${CONSENTIMIENTO_GENERAL_VERSION}::${CONSENTIMIENTO_GENERAL_ID}::${CONSENTIMIENTO_GENERAL_TXT}`
        );

        const consentimientoBase = this.mostrarConsentimientoBase
          ? {
              id: CONSENTIMIENTO_GENERAL_ID,
              version: CONSENTIMIENTO_GENERAL_VERSION,
              aceptado: true,
              fechaISO: this.todayISO(),
              textoRellenoTXT,
              hashBase,
              esMenor,
              responsable: esMenor
                ? {
                    parentesco: answersPayload.respParentesco || "",
                    licencia: answersPayload.respLicencia || "",
                  }
                : null,
            }
          : null;

        const data = {
          fullName: this.fullName.trim(),
          ...answersPayload,
          edad: answersPayload.edad ? Number(answersPayload.edad) : null,
          answers: answersPayload,
          consentimientoBase,
          createdAt: new Date().toISOString(),
          activo: false,
        };

        const esLaser = servicioSeleccionado === "depilacion_laser";

        if (esLaser) {
          // === LÁSER (MODIFICADO: SIN FUNCIÓN DE CORREO) ===
const docRef = await addDoc(collection(db, "prospectos"), data);
console.log("✅ Prospecto creado con ID:", docRef.id); // opcional, para debug

// limpiar formulario
this.resetAnswers();
this.fullName = "";
this.currentStep = 0;
this.consentimientoAceptado = false;
this.validationErrors = {};
this.renderConsentimientoBase();

// redirigir a página de confirmación
await this.$router.push({ path: "/solicitud-completada" });

        } else {
          // === NO-LÁSER (EVITAR DUPLICADOS): NO crear doc en 'pacientes' aquí ===
          const docRef = await addDoc(collection(db, "prePacientes"), data);
          await this.$router.push({
            path: `/registro/crear-cuenta/${docRef.id}`,
            query: { email: data.email },
          });

          this.resetAnswers();
          this.fullName = "";
          this.currentStep = 0;
          this.consentimientoAceptado = false;
          this.validationErrors = {};
          this.renderConsentimientoBase();
        }

        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.error("Error al enviar:", error);
        alert("No se pudo enviar. Intente nuevamente.");
      }
    },
  },

  mounted() {
    // Inicial: si ya hay fecha, calcula edad; render consentimiento
    if (this.answers.fechaNacimiento) {
      this.answers.edad = this.calcEdad(this.answers.fechaNacimiento);
    }
    this.renderConsentimientoBase();
  },
};
</script>

<style scoped>
.bg-login-image {
  background-image: url("/public/fondologin.jpg");
  background-size: cover;
  background-position: center;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

button i {
  font-size: 1.25rem;
}
</style>
